import {
  auditEntrySchema,
  DEFAULT_TIME_ZONE,
  catalogSyncCandidateSchema,
  catalogSyncRunSchema,
  catalogSyncSummarySchema,
  categorySchema,
  contentPageSchema,
  contentPageSummarySchema,
  customerAdminDetailSchema,
  customerAdminSummarySchema,
  homeSlideSchema,
  inviteMemberResponseSchema,
  issuedInvitationSchema,
  membershipSchema,
  orderAdminDetailSchema,
  orderAdminSummarySchema,
  orderQuoteSchema,
  pageResponseSchema,
  productCategoryGroupsSchema,
  popupSchema,
  productDetailSchema,
  productFlyerSchema,
  productSummarySchema,
  storeBrandingSchema,
  storeDashboardSchema,
  storeHomeSchema,
  serviceTicketDetailSchema,
  serviceTicketListSchema,
  storeMailSchema,
  storeSettingsSchema,
  tenantMediaSchema,
  type ConnectStoreMailRequest,
  type CreateCategoryRequest,
  type CreateContentPageRequest,
  type CreateCustomProductRequest,
  type CreateHomeSlideRequest,
  type CreatePopupRequest,
  type CustomerStatus,
  type InviteMemberRequest,
  type OpenServiceCallRequest,
  type NotShownReason,
  type OrderStatus,
  type ProductOrigin,
  type ProductSourceState,
  type ProductStatus,
  type ReplaceProductMediaRequest,
  type SetProductLogoRequest,
  type SaveOrderQuoteRequest,
  type StartCatalogSyncRequest,
  type StockFilter,
  type TenantRole,
  type UpdateCategoryRequest,
  type UpdateContentPageRequest,
  type UpdateCustomerRequest,
  type UpdateHomeSlideRequest,
  type UpdateOrderRequest,
  type UpdatePopupRequest,
  type UpdateProductRequest,
  type UpdateStoreBrandingRequest,
  type UpdateStoreHomeRequest,
  type UpdateStoreSettingsRequest,
} from '@avrahami/contracts';
import { withQuery } from '@avrahami/ui';
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';
import { z } from 'zod';
import { activeTenant, api } from '../lib/api';
import { useCan } from './session';

/**
 * The tenant surface (`/v1/tenant/*`). The store is always the session's active tenant: nothing
 * here names it. Every answer is validated with the shared contracts.
 */

const PAGE_SIZE = 25;
/** Lists start without a cursor. */
const FIRST_PAGE: string | undefined = undefined;

function itemsOf<Item extends z.ZodType>(item: Item) {
  return z.object({ items: z.array(item) });
}

function path(base: string, id: string): string {
  return `${base}/${encodeURIComponent(id)}`;
}

/**
 * Query keys in one place, so mutations invalidate exactly what they change. Every key starts
 * with the active store: answers cached for one store are never shown for another, even for the
 * moment between a store switch and the cache being cleared.
 */
function scoped<const Parts extends readonly unknown[]>(...parts: Parts) {
  return ['tenant', activeTenant() ?? 'none', ...parts] as const;
}

export const keys = {
  all: () => scoped(),
  dashboard: () => scoped('dashboard'),
  orders: () => scoped('orders'),
  order: (id: string) => scoped('orders', 'detail', id),
  orderQuote: (id: string) => scoped('orders', 'detail', id, 'quote'),
  quoteProducts: (q: string) => scoped('products', 'quote-picker', q),
  customers: () => scoped('customers'),
  customer: (id: string) => scoped('customers', 'detail', id),
  products: () => scoped('products'),
  product: (id: string) => scoped('products', 'detail', id),
  // Under the product: a change of the product (its copy, its price) draws the flyer again.
  productFlyer: (id: string) => scoped('products', 'detail', id, 'flyer'),
  categories: () => scoped('categories'),
  catalogSync: () => scoped('catalog-sync'),
  catalogSyncSummary: () => scoped('catalog-sync', 'summary'),
  catalogSyncCandidates: () => scoped('catalog-sync', 'candidates'),
  popups: () => scoped('popups'),
  settings: () => scoped('settings'),
  storeMail: () => scoped('settings', 'mail'),
  branding: () => scoped('branding'),
  home: () => scoped('home'),
  pages: () => scoped('pages'),
  page: (id: string) => scoped('pages', 'detail', id),
  members: () => scoped('members'),
  audit: () => scoped('audit'),
  service: () => scoped('service'),
  serviceTicket: (id: string) => scoped('service', 'detail', id),
};

function pagedQuery<Item extends z.ZodType>(
  key: readonly unknown[],
  base: string,
  item: Item,
  filters: Readonly<Record<string, string | undefined>>,
) {
  const page = pageResponseSchema(item);
  return infiniteQueryOptions({
    queryKey: [...key, 'list', filters],
    queryFn: ({ pageParam }) =>
      api.request(
        'GET',
        withQuery(base, { ...filters, limit: String(PAGE_SIZE), cursor: pageParam }),
        page,
      ),
    initialPageParam: FIRST_PAGE,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

// ---------------------------------------------------------------------------------------------
// Dashboard, quote requests (the API's "orders"), customers
// ---------------------------------------------------------------------------------------------

export function dashboardQuery() {
  return queryOptions({
    queryKey: keys.dashboard(),
    queryFn: () => api.request('GET', '/v1/tenant/dashboard', storeDashboardSchema),
    refetchInterval: 60_000,
  });
}

export interface OrderFilters {
  status: OrderStatus | '';
  q: string;
  from: string;
  to: string;
}

export const NO_ORDER_FILTERS: OrderFilters = {
  status: '',
  q: '',
  from: '',
  to: '',
};

export function orderListQuery(filters: OrderFilters) {
  return pagedQuery(keys.orders(), '/v1/tenant/orders', orderAdminSummarySchema, {
    status: filters.status,
    q: filters.q.trim(),
    from: filters.from,
    to: filters.to,
  });
}

export function orderQuery(id: string) {
  return queryOptions({
    queryKey: keys.order(id),
    queryFn: () => api.request('GET', path('/v1/tenant/orders', id), orderAdminDetailSchema),
  });
}

/** The store's quote for the request: the saved one, or the request at its list prices. */
export function orderQuoteQuery(id: string) {
  return queryOptions({
    queryKey: keys.orderQuote(id),
    queryFn: () => api.request('GET', `${path('/v1/tenant/orders', id)}/quote`, orderQuoteSchema),
  });
}

/** Products of the store to add to a quote: the first ones matching the text. */
export function quoteProductsQuery(q: string) {
  return queryOptions({
    queryKey: keys.quoteProducts(q),
    queryFn: () =>
      api.request(
        'GET',
        withQuery('/v1/tenant/products', { q, limit: '10' }),
        pageResponseSchema(productSummarySchema),
      ),
    enabled: q.length > 0,
    placeholderData: keepPreviousData,
  });
}

export interface CustomerFilters {
  q: string;
  status: CustomerStatus | '';
}

export function customerListQuery(filters: CustomerFilters) {
  return pagedQuery(keys.customers(), '/v1/tenant/customers', customerAdminSummarySchema, {
    q: filters.q.trim(),
    status: filters.status,
  });
}

export function customerQuery(id: string) {
  return queryOptions({
    queryKey: keys.customer(id),
    queryFn: () => api.request('GET', path('/v1/tenant/customers', id), customerAdminDetailSchema),
  });
}

// ---------------------------------------------------------------------------------------------
// Products and categories
// ---------------------------------------------------------------------------------------------

export interface ProductFilters {
  q: string;
  status: ProductStatus | '';
  origin: ProductOrigin | '';
  categoryId: string;
  stock: StockFilter | '';
  sourceState: ProductSourceState | '';
  /** Only products not shown in the store, for this reason. */
  notShown: NotShownReason | '';
}

export const NO_PRODUCT_FILTERS: ProductFilters = {
  q: '',
  status: '',
  origin: '',
  categoryId: '',
  stock: '',
  sourceState: '',
  notShown: '',
};

export function productListQuery(filters: ProductFilters) {
  return pagedQuery(keys.products(), '/v1/tenant/products', productSummarySchema, {
    q: filters.q.trim(),
    status: filters.status,
    origin: filters.origin,
    categoryId: filters.categoryId,
    stock: filters.stock,
    sourceState: filters.sourceState,
    notShown: filters.notShown,
  });
}

/**
 * The store's categories with the products of the list counted in each, under the same filters
 * (the list is shown under them; a search shows its results without categories).
 */
export function productGroupsQuery(filters: ProductFilters) {
  const params = {
    status: filters.status,
    origin: filters.origin,
    stock: filters.stock,
    sourceState: filters.sourceState,
    notShown: filters.notShown,
  };
  return queryOptions({
    queryKey: [...keys.products(), 'groups', params],
    queryFn: () =>
      api.request(
        'GET',
        withQuery('/v1/tenant/products/categories', params),
        productCategoryGroupsSchema,
      ),
    // The categories stay (and stay open) while they are counted again under other filters.
    placeholderData: keepPreviousData,
  });
}

export function productQuery(id: string) {
  return queryOptions({
    queryKey: keys.product(id),
    queryFn: () => api.request('GET', path('/v1/tenant/products', id), productDetailSchema),
  });
}

/** What the product's flyer is drawn from (docs/FLYERS.md): the store's template and product page. */
export function productFlyerQuery(id: string) {
  return queryOptions({
    queryKey: keys.productFlyer(id),
    queryFn: () =>
      api.request('GET', `${path('/v1/tenant/products', id)}/flyer`, productFlyerSchema),
  });
}

export function categoriesQuery() {
  return queryOptions({
    queryKey: keys.categories(),
    queryFn: () => api.request('GET', '/v1/tenant/categories', itemsOf(categorySchema)),
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------------------------
// Pull new products (catalog sync)
// ---------------------------------------------------------------------------------------------

/** How often a pull in progress is read again. */
const SYNC_POLL_MS = 2_000;

/** What the catalog has that the store does not, and the pull that is running or ended last. */
export function catalogSyncSummaryQuery() {
  return queryOptions({
    queryKey: keys.catalogSyncSummary(),
    queryFn: () => api.request('GET', '/v1/tenant/catalog-sync', catalogSyncSummarySchema),
    // A pull in progress is followed until it ends; otherwise nothing is polled.
    refetchInterval: (query) => {
      const summary = query.state.data;
      return summary !== undefined && summary.activeRun !== null ? SYNC_POLL_MS : false;
    },
  });
}

export interface CatalogSyncFilters {
  q: string;
  /** Only products added to the catalog since the store's last pull. */
  onlyNew: boolean;
}

export const NO_CATALOG_SYNC_FILTERS: CatalogSyncFilters = { q: '', onlyNew: false };

/** The catalog products the store can add, newest first; `categoryId` for one category's. */
export function catalogSyncCandidatesQuery(filters: CatalogSyncFilters, categoryId = '') {
  return pagedQuery(
    keys.catalogSyncCandidates(),
    '/v1/tenant/catalog-sync/candidates',
    catalogSyncCandidateSchema,
    { q: filters.q.trim(), onlyNew: filters.onlyNew ? 'true' : undefined, categoryId },
  );
}

/** The catalog's categories with the products the store can add counted in each. */
export function catalogSyncGroupsQuery(onlyNew: boolean) {
  return queryOptions({
    queryKey: [...keys.catalogSync(), 'groups', onlyNew],
    queryFn: () =>
      api.request(
        'GET',
        withQuery('/v1/tenant/catalog-sync/categories', { onlyNew: onlyNew ? 'true' : undefined }),
        productCategoryGroupsSchema,
      ),
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------------------------
// Small entities replaced whole (PUT with the version read)
// ---------------------------------------------------------------------------------------------

function listQueryOf<Item extends z.ZodType>(base: string, item: Item, key: readonly unknown[]) {
  return queryOptions({
    queryKey: key,
    queryFn: () => api.request('GET', base, itemsOf(item)),
  });
}

/**
 * A list of small entities with create, replace and delete (`{ items }`, unpaged). The calls are
 * arrow properties, so they can be handed around as plain functions.
 */
class Resource<Item extends z.ZodType, Create, Update> {
  private readonly base: string;
  private readonly item: Item;
  private readonly key: () => readonly unknown[];

  constructor(base: string, item: Item, key: () => readonly unknown[]) {
    this.base = base;
    this.item = item;
    this.key = key;
  }

  readonly listQuery = () => listQueryOf(this.base, this.item, this.key());

  readonly create = (body: Create) => api.request('POST', this.base, this.item, body);
  readonly update = (id: string, body: Update) =>
    api.request('PUT', path(this.base, id), this.item, body);
  readonly remove = (id: string) => api.send('DELETE', path(this.base, id));
}

export const popups = new Resource<typeof popupSchema, CreatePopupRequest, UpdatePopupRequest>(
  '/v1/tenant/popups',
  popupSchema,
  keys.popups,
);

export function contentPagesQuery() {
  return queryOptions({
    queryKey: keys.pages(),
    queryFn: () => api.request('GET', '/v1/tenant/pages', itemsOf(contentPageSummarySchema)),
  });
}

export function contentPageQuery(id: string) {
  return queryOptions({
    queryKey: keys.page(id),
    queryFn: () => api.request('GET', path('/v1/tenant/pages', id), contentPageSchema),
  });
}

export function settingsQuery() {
  return queryOptions({
    queryKey: keys.settings(),
    queryFn: () => api.request('GET', '/v1/tenant/settings', storeSettingsSchema),
  });
}

/** How often the store's calls are read again: an answer from the platform shows up by itself. */
const SERVICE_POLL_MS = 60_000;
const SERVICE_BASE = '/v1/tenant/service-tickets';

/** The store's own service calls to the platform (adr/0036). */
export function serviceCallsQuery() {
  return queryOptions({
    queryKey: keys.service(),
    queryFn: () => api.request('GET', SERVICE_BASE, serviceTicketListSchema),
    refetchInterval: SERVICE_POLL_MS,
  });
}

export function serviceCallQuery(id: string) {
  return queryOptions({
    queryKey: keys.serviceTicket(id),
    queryFn: () => api.request('GET', path(SERVICE_BASE, id), serviceTicketDetailSchema),
    refetchInterval: SERVICE_POLL_MS,
  });
}

export function storeMailQuery() {
  return queryOptions({
    queryKey: keys.storeMail(),
    queryFn: () => api.request('GET', '/v1/tenant/settings/mail', storeMailSchema),
  });
}

export function brandingQuery() {
  return queryOptions({
    queryKey: keys.branding(),
    queryFn: () => api.request('GET', '/v1/tenant/branding', storeBrandingSchema),
  });
}

export function homeQuery() {
  return queryOptions({
    queryKey: keys.home(),
    queryFn: () => api.request('GET', '/v1/tenant/home', storeHomeSchema),
  });
}

export function membersQuery() {
  return queryOptions({
    queryKey: keys.members(),
    queryFn: () => api.request('GET', '/v1/tenant/memberships', itemsOf(membershipSchema)),
  });
}

export function auditQuery() {
  return infiniteQueryOptions({
    queryKey: keys.audit(),
    queryFn: ({ pageParam }) =>
      api.request(
        'GET',
        withQuery('/v1/tenant/audit-log', { limit: String(PAGE_SIZE), cursor: pageParam }),
        pageResponseSchema(auditEntrySchema),
      ),
    initialPageParam: FIRST_PAGE,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export const tenantApi = {
  updateOrder: (id: string, body: UpdateOrderRequest) =>
    api.request('PATCH', path('/v1/tenant/orders', id), orderAdminDetailSchema, body),
  // The card is read again afterwards (it carries the quote requests too).
  updateCustomer: (id: string, body: UpdateCustomerRequest) =>
    api.send('PATCH', path('/v1/tenant/customers', id), body),

  createProduct: (body: CreateCustomProductRequest) =>
    api.request('POST', '/v1/tenant/products', productDetailSchema, body),
  updateProduct: (id: string, body: UpdateProductRequest) =>
    api.request('PATCH', path('/v1/tenant/products', id), productDetailSchema, body),
  // Answers the product with its new version and image ids.
  replaceProductMedia: (id: string, body: ReplaceProductMediaRequest) =>
    api.request('PUT', `${path('/v1/tenant/products', id)}/media`, productDetailSchema, body),
  setProductLogo: (id: string, body: SetProductLogoRequest) =>
    api.request('PUT', `${path('/v1/tenant/products', id)}/logo`, productDetailSchema, body),
  /** The quote request as a PDF (fetched through the client, so failures are ApiErrors). */
  orderDocument: (id: string) => api.blob(`${path('/v1/tenant/orders', id)}/document`),
  saveOrderQuote: (id: string, body: SaveOrderQuoteRequest) =>
    api.request('PUT', `${path('/v1/tenant/orders', id)}/quote`, orderQuoteSchema, body),
  /** The store's quote as a PDF, to send to the shopper. */
  orderQuoteDocument: (id: string) => api.blob(`${path('/v1/tenant/orders', id)}/quote/document`),

  createCategory: (body: CreateCategoryRequest) =>
    api.request('POST', '/v1/tenant/categories', categorySchema, body),
  updateCategory: (id: string, body: UpdateCategoryRequest) =>
    api.request('PATCH', path('/v1/tenant/categories', id), categorySchema, body),
  deleteCategory: (id: string) => api.send('DELETE', path('/v1/tenant/categories', id)),

  // Answers the pull as it was queued; the summary follows its progress.
  startCatalogSync: (body: StartCatalogSyncRequest) =>
    api.request('POST', '/v1/tenant/catalog-sync/runs', catalogSyncRunSchema, body),

  uploadMedia: (file: Blob) => api.upload('/v1/tenant/media', file, tenantMediaSchema),

  updateSettings: (body: UpdateStoreSettingsRequest) =>
    api.request('PUT', '/v1/tenant/settings', storeSettingsSchema, body),
  connectStoreMail: (body: ConnectStoreMailRequest) =>
    api.request('PUT', '/v1/tenant/settings/mail', storeMailSchema, body),
  disconnectStoreMail: () => api.request('DELETE', '/v1/tenant/settings/mail', storeMailSchema),
  testStoreMail: () => api.request('POST', '/v1/tenant/settings/mail/test', storeMailSchema),
  openServiceCall: (body: OpenServiceCallRequest) =>
    api.request('POST', SERVICE_BASE, serviceTicketDetailSchema, body),
  replyServiceCall: (id: string, body: string) =>
    api.request('POST', `${path(SERVICE_BASE, id)}/messages`, serviceTicketDetailSchema, {
      body,
    }),
  updateBranding: (body: UpdateStoreBrandingRequest) =>
    api.request('PUT', '/v1/tenant/branding', storeBrandingSchema, body),
  updateHome: (body: UpdateStoreHomeRequest) =>
    api.request('PUT', '/v1/tenant/home', storeHomeSchema, body),
  createSlide: (body: CreateHomeSlideRequest) =>
    api.request('POST', '/v1/tenant/home/slides', homeSlideSchema, body),
  updateSlide: (id: string, body: UpdateHomeSlideRequest) =>
    api.request('PUT', path('/v1/tenant/home/slides', id), homeSlideSchema, body),
  deleteSlide: (id: string) => api.send('DELETE', path('/v1/tenant/home/slides', id)),

  createPage: (body: CreateContentPageRequest) =>
    api.request('POST', '/v1/tenant/pages', contentPageSchema, body),
  updatePage: (id: string, body: UpdateContentPageRequest) =>
    api.request('PUT', path('/v1/tenant/pages', id), contentPageSchema, body),
  deletePage: (id: string) => api.send('DELETE', path('/v1/tenant/pages', id)),

  inviteMember: (body: InviteMemberRequest) =>
    api.request('POST', '/v1/tenant/memberships', inviteMemberResponseSchema, body),
  reissueInvitation: (membershipId: string) =>
    api.request(
      'POST',
      `${path('/v1/tenant/memberships', membershipId)}/invitation`,
      issuedInvitationSchema,
    ),
  changeRole: (membershipId: string, role: TenantRole) =>
    api.request('PATCH', path('/v1/tenant/memberships', membershipId), membershipSchema, { role }),
  removeMember: (membershipId: string) =>
    api.send('DELETE', path('/v1/tenant/memberships', membershipId)),
};

/**
 * The store's own time zone (its settings): every date and time the admin shows or reads is in
 * it, whatever the browser's zone. Until the settings arrive, the platform default.
 */
export function useStoreTimeZone(): string {
  const canRead = useCan('store.settings.read');
  const settings = useQuery({ ...settingsQuery(), enabled: canRead });
  return settings.data?.timeZone ?? DEFAULT_TIME_ZONE;
}
